// <bundle-selector-component> — quantity-break / bundle picker for the product page.
// Registered globally so it also upgrades when morphed into the Quick Add modal.

import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent } from '@shopify/events';

if (!customElements.get('bundle-selector-component')) {
  class BundleSelectorComponent extends HTMLElement {
    static get observedAttributes() {
      return ['data-unit-cents', 'data-variant-prices'];
    }

    connectedCallback() {
      if (!this._delegated) {
        this._delegated = true;

        this.addEventListener('click', (event) => {
          const tier = event.target.closest('[data-tier]');
          if (tier && this.contains(tier)) this.selectTier(tier);
        });

        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          const tier = event.target.closest('[data-tier]');
          if (tier && this.contains(tier)) {
            event.preventDefault();
            this.selectTier(tier);
          }
        });

        this._onProductSelect = (event) => {
          if (!event || !event.promise) return;
          event.promise
            .then((res) => {
              const detail = res && res.detail;
              const id = detail && detail.resource && detail.resource.id;
              if (!id) return;
              const idInput = this.querySelector('.bundle-selector__form input[name="id"]');
              if (idInput) idInput.value = id;
              if (this._priceMap[id] != null) {
                this._unitCents = this._priceMap[id];
                this._compareUnitCents = this._compareMap[id] || this._unitCents;
                this.render();
              }
            })
            .catch(() => {});
        };

        this._scope = this.closest('.shopify-section, dialog, quick-add-dialog') || document;
        this._scope.addEventListener('shopify:product:select', this._onProductSelect);

        this.addEventListener('change', (event) => {
          if (event.target instanceof HTMLSelectElement && event.target.matches('[data-unit-color-select]')) {
            this._syncMediaForVariant(event.target.value);
          }
        });

        this._bindFormSubmit();
        this._initStickyBar();
        this._bindBuyNow();
      }

      this._readConfig();
      this.render();
    }

    // "Buy it now": clicks the real Add to cart button (so mixed colors, bundle properties and the free
    // gift all go through the normal path), waits until the cart holds the bundle and the free-bands gift
    // the gift guard owes it, then sends the shopper to checkout.
    _bindBuyNow() {
      const button = this.querySelector('[data-buy-now]');
      const atcButton = this.querySelector('.bundle-selector__atc');
      if (!button || !atcButton || button._bound) return;
      button._bound = true;

      const readCart = async () => {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
        return res.json();
      };

      const giftOwed = (cart) => {
        let config = null;
        try {
          config = JSON.parse(document.getElementById('gift-guard-config')?.textContent || 'null');
        } catch (e) {
          config = null;
        }
        if (!config || !config.giftVariantId) return true;
        const trainers = cart.items
          .filter((item) => item.handle === config.trainerHandle)
          .reduce((sum, item) => sum + item.quantity, 0);
        const gifts = cart.items
          .filter((item) => item.variant_id === Number(config.giftVariantId))
          .reduce((sum, item) => sum + item.quantity, 0);
        return gifts >= Math.floor(trainers / (config.perGift || 3));
      };

      button.addEventListener('click', async () => {
        if (button.disabled || atcButton.disabled) return;
        const label = button.textContent;
        button.disabled = true;
        button.textContent = 'Taking you to checkout…';

        try {
          const before = (await readCart()).item_count;
          atcButton.click();

          const deadline = Date.now() + 10000;
          while (Date.now() < deadline) {
            await new Promise((resolve) => setTimeout(resolve, 450));
            const cart = await readCart();
            if (cart.item_count > before && giftOwed(cart)) {
              window.location.href = '/checkout';
              return;
            }
          }
        } catch (e) {
          /* fall through and let the shopper try again */
        }
        button.disabled = false;
        button.textContent = label;
      });
    }

    // Floating add to cart bar for the product page. It shows the selected
    // bundle + price and clicks the real Add to cart button, so mixed colors
    // and gifts go through exactly the same path. Not built inside the Quick
    // Add modal, which already pins its own button.
    _initStickyBar() {
      const atcButton = this.querySelector('.bundle-selector__atc');
      if (!atcButton || this.closest('dialog, quick-add-dialog')) return;

      const bar = document.createElement('div');
      bar.className = 'bundle-sticky-bar';
      bar.dataset.visible = 'false';
      bar.setAttribute('role', 'region');
      bar.setAttribute('aria-label', 'Add to cart');
      bar.innerHTML =
        '<div class="bundle-sticky-bar__info">' +
        '<span class="bundle-sticky-bar__label"></span>' +
        '<span class="bundle-sticky-bar__price"></span>' +
        '</div>' +
        '<button type="button" class="bundle-sticky-bar__button">Add to cart</button>';

      const accent = getComputedStyle(this.querySelector('.bundle-selector') || this).getPropertyValue('--bundle-accent');
      if (accent) bar.style.setProperty('--bundle-accent', accent.trim());

      document.body.appendChild(bar);
      this._stickyBar = bar;

      const button = bar.querySelector('.bundle-sticky-bar__button');
      button.addEventListener('click', () => atcButton.click());

      let atcVisible = false;
      let footerVisible = false;
      const update = () => {
        // Visible from the moment the page opens (so Add to cart never needs a scroll), and hidden only
        // while the real Add to cart button or the footer is on screen.
        const show = !atcVisible && !footerVisible;
        bar.dataset.visible = String(show);
        button.disabled = atcButton.disabled;
      };

      this._stickyUpdate = update;
      this._stickyObserver = new IntersectionObserver((entries) => {
        atcVisible = entries[entries.length - 1].isIntersecting;
        update();
      });
      this._stickyObserver.observe(atcButton);

      const footer = document.querySelector('footer');
      if (footer) {
        this._stickyFooterObserver = new IntersectionObserver((entries) => {
          footerVisible = entries[entries.length - 1].isIntersecting;
          update();
        });
        this._stickyFooterObserver.observe(footer);
      }

      this._stickyScroll = update;
      // Capture so scrolls inside .page-wrapper are seen too (they don't bubble).
      document.addEventListener('scroll', this._stickyScroll, { passive: true, capture: true });
    }

    _updateStickyBar(label, saleCents, compareCents) {
      const bar = this._stickyBar;
      if (!bar) return;

      bar.querySelector('.bundle-sticky-bar__label').textContent = label;
      const price = bar.querySelector('.bundle-sticky-bar__price');
      price.textContent = this.formatMoney(saleCents);
      if (compareCents > saleCents) {
        const was = document.createElement('s');
        was.textContent = this.formatMoney(compareCents);
        price.appendChild(was);
      }
      if (this._stickyUpdate) this._stickyUpdate();
    }

    disconnectedCallback() {
      if (this._stickyBar) {
        this._stickyBar.remove();
        this._stickyBar = null;
      }
      if (this._stickyObserver) this._stickyObserver.disconnect();
      if (this._stickyFooterObserver) this._stickyFooterObserver.disconnect();
      if (this._stickyScroll) document.removeEventListener('scroll', this._stickyScroll, { capture: true });

      if (this._scope && this._onProductSelect) {
        this._scope.removeEventListener('shopify:product:select', this._onProductSelect);
      }
      this._delegated = false;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue || !this._delegated) return;
      this._readConfig();
      this.render();
    }

    _readConfig() {
      this._unitCents = parseInt(this.dataset.unitCents, 10) || 0;
      this._compareUnitCents = parseInt(this.dataset.compareUnitCents, 10) || this._unitCents;
      const brace = String.fromCharCode(123, 123) + 'amount' + String.fromCharCode(125, 125);
      this._moneyFormat = '$' + brace;
      try {
        this._moneyFormat = JSON.parse(this.dataset.moneyFormat) || this._moneyFormat;
      } catch (e) {
        /* keep fallback */
      }
      this._priceMap = {};
      try {
        this._priceMap = JSON.parse(this.dataset.variantPrices) || {};
      } catch (e) {
        /* keep empty */
      }
      this._compareMap = {};
      try {
        this._compareMap = JSON.parse(this.dataset.variantCompares) || {};
      } catch (e) {
        /* keep empty */
      }
    }

    formatMoney(cents) {
      cents = Math.round(cents || 0);
      const fmt = this._moneyFormat;

      const thousands = (num, precision, thousandsSep, decimalSep) => {
        precision = precision == null ? 2 : precision;
        thousandsSep = thousandsSep == null ? ',' : thousandsSep;
        decimalSep = decimalSep == null ? '.' : decimalSep;
        const parts = (Math.abs(num) / 100).toFixed(precision).split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousandsSep);
        const centsPart = parts[1] ? decimalSep + parts[1] : '';
        return (num < 0 ? '-' : '') + dollars + centsPart;
      };

      const match = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
      const placeholder = match ? match[1] : 'amount';
      let value;
      switch (placeholder) {
        case 'amount_no_decimals':
          value = thousands(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = thousands(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = thousands(cents, 0, '.', ',');
          break;
        case 'amount_with_apostrophe_separator':
          value = thousands(cents, 2, "'", '.');
          break;
        case 'amount_with_space_separator':
          value = thousands(cents, 2, ' ', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = thousands(cents, 0, ' ', '');
          break;
        default:
          value = thousands(cents, 2);
      }
      return fmt.replace(/\{\{\s*\w+\s*\}\}/, value);
    }

    // "SAVE $18" — drop a trailing .00 on whole-dollar amounts.
    formatSave(cents) {
      const whole = Math.round(cents) % 100 === 0;
      return whole ? this.formatMoney(cents).replace(/[.,]00\b/, '') : this.formatMoney(cents);
    }

    // Each unit in a multi-pack has its own independent color <select> (see
    // the tier markup). Intercepts the Add to cart button's click — before
    // any submit event exists — to check whether the shopper picked
    // different colors for different units. If so, one line item per
    // color/quantity combo is added directly through the cart API instead
    // of the single id+quantity the plain form supports.
    //
    // This has to happen at the click, not the form's submit event: the
    // theme's `on:submit="/handleSubmit"` bindings are all wired through a
    // single capturing listener on `document` (see component.js), which
    // always fires before any bubble-phase listener we could attach
    // ourselves — so by the time a submit listener of ours would run, the
    // default single-item add has already happened. Calling
    // preventDefault() on the click itself cancels the submit button's
    // activation behavior (form submission) before it's ever dispatched.
    _bindFormSubmit() {
      const atcButton = this.querySelector('.bundle-selector__atc');
      if (!atcButton) return;

      atcButton.addEventListener('click', (event) => {
        const selectedTier = this.querySelector('[data-tier].is-selected');
        const selects = selectedTier
          ? Array.from(selectedTier.querySelectorAll('[data-unit-color-select]'))
          : [];
        // A tier that includes a gift product can't use the plain form
        // submit (one variant id + quantity), so it goes through the
        // multi-item cart request below with the gift added as its own line.
        const giftEl = selectedTier ? selectedTier.querySelector('[data-gift][data-gift-variant-id]') : null;
        const giftVariantId = giftEl ? giftEl.dataset.giftVariantId : null;
        if (!selects.length && !giftVariantId) return;

        const idInput = this.querySelector('.bundle-selector__form input[name="id"]');
        const colorsInput = this.querySelector('.bundle-selector__form input[name="properties[Colors]"]');

        const tierQty = selectedTier ? Number(selectedTier.dataset.qty) || 1 : 1;
        const unitVariantIds = selects.length
          ? selects.map((select) => select.value)
          : Array(tierQty).fill(idInput ? idInput.value : '');
        const unitColorNames = selects.map((select) => {
          const option = select.options[select.selectedIndex];
          return option ? option.textContent.trim() : '';
        });
        const allSame = unitVariantIds.every((id) => id === unitVariantIds[0]);

        if (allSame && !giftVariantId) {
          if (idInput) idInput.value = unitVariantIds[0];
          // Same color for every unit — the variant name already says it
          // (e.g. "Purple"), so a "Colors: Purple, Purple" property would
          // just be noise. Only show it when the units actually differ.
          if (colorsInput) {
            colorsInput.disabled = true;
            colorsInput.value = '';
          }
          return;
        }

        event.preventDefault();
        const bundleLabel = selectedTier
          ? selectedTier.dataset.label +
            (Number(selectedTier.dataset.discount) ? ' (save ' + selectedTier.dataset.discount + '%)' : '')
          : '';
        this._submitMixedBundle(unitVariantIds, unitColorNames, bundleLabel, giftVariantId);
      });
    }

    // Picking a color in a unit picker doesn't go through the real
    // <variant-picker> component — each unit needs its own independent
    // choice, which that singleton element can't provide — so the gallery
    // image swap that normally comes with it doesn't happen for free. This
    // reproduces it: fetch the product page pre-selected to the chosen
    // variant and dispatch the same product-select event the real picker
    // uses, so media-gallery.js and product-price.js update themselves
    // exactly as they already do elsewhere on the site.
    _syncMediaForVariant(variantId) {
      const form = this.querySelector('.bundle-selector__form');
      const productUrl = form && form.dataset.productUrl;
      if (!productUrl || !variantId) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);

      const deferred = ProductSelectEvent.createPromise();

      this.dispatchEvent(
        new ProductSelectEvent({
          product: {
            id: (form && form.dataset.productId) || '',
            title: '',
            handle: '',
          },
          selectedOptions: [],
          detail: { variantId },
          promise: deferred.promise,
        })
      );

      fetch(url.toString())
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          deferred.resolve({
            variant: null,
            detail: {
              html,
              productId: (form && form.dataset.productId) || '',
              resource: { id: variantId },
            },
          });
        })
        .catch(() => {});
    }

    _groupUnitsByVariant(variantIds) {
      const counts = new Map();
      for (const id of variantIds) counts.set(id, (counts.get(id) || 0) + 1);
      return Array.from(counts.entries()).map(([id, quantity]) => ({ id, quantity }));
    }

    // Mirrors ProductFormComponent's own cart refresh: prefer the cart
    // drawer's own fetch so its UI updates the same way it does for every
    // other add-to-cart on the site, falling back to a plain fetch when
    // there's no drawer on the page.
    async _refreshCart() {
      const cartItemsComponent = document.querySelector('cart-items-component');
      if (cartItemsComponent) {
        await customElements.whenDefined('cart-items-component');
        return cartItemsComponent.fetchCartData();
      }

      return fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      }).then((response) => {
        if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
        return response.json();
      });
    }

    _submitMixedBundle(variantIds, colorNames, bundleLabel, giftVariantId) {
      const items = this._groupUnitsByVariant(variantIds);
      const atcButton = this.querySelector('.bundle-selector__atc');
      if (atcButton) atcButton.disabled = true;

      const colorsDiffer = variantIds.some((id) => id !== variantIds[0]);
      const properties = {};
      if (bundleLabel) properties['_bundle'] = bundleLabel;
      if (colorsDiffer && colorNames && colorNames.length > 1) {
        properties['Colors'] = colorNames.join(', ');
        // Ties this bundle's lines together so gift-guard.js can keep the Colors
        // label accurate when units are later removed.
        properties['_bundle_id'] = Math.random().toString(36).slice(2, 10);
      }

      // The gift goes in as its own line. The line item itself can't be made
      // free from the theme — a Shopify discount does that — so it's tagged
      // here only so it's identifiable on the order.
      // _gift_for ("product-handle:count") lets the cart guard (gift-guard.js)
      // remove the gift again if the qualifying quantity is later reduced.
      const productForm = this.querySelector('.bundle-selector__form');
      const productHandle = productForm && productForm.dataset.productUrl
        ? productForm.dataset.productUrl.split('?')[0].split('/').filter(Boolean).pop()
        : '';
      const selectedTierEl = this.querySelector('[data-tier].is-selected');
      const giftLine = giftVariantId
        ? {
            id: Number(giftVariantId),
            quantity: 1,
            properties: {
              _gift: 'Free with ' + (bundleLabel || 'bundle'),
              _gift_for: productHandle + ':' + ((selectedTierEl && selectedTierEl.dataset.qty) || variantIds.length),
            },
          }
        : null;

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset && el.dataset.sectionId)
        .filter(Boolean);

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items
            .map((item) => ({ merchandiseId: item.id, quantity: item.quantity }))
            .concat(giftLine ? [{ merchandiseId: String(giftLine.id), quantity: 1 }] : []),
          promise: deferred.promise,
        })
      );

      fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items
            .map((item) => ({
              id: Number(item.id),
              quantity: item.quantity,
              properties: Object.keys(properties).length ? properties : undefined,
            }))
            .concat(giftLine ? [giftLine] : []),
          sections: sectionIds.join(','),
        }),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            const error = new Error(response.message || 'Add to cart failed');
            deferred.reject(error);
            this.dispatchEvent(
              new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' })
            );
            return;
          }

          return this._refreshCart()
            .then((cart) => (giftLine ? this._dropGiftIfNotFree(cart, giftLine.id) : cart))
            .then((cart) => {
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                items: cart.items,
                source: 'bundle-selector-component',
                sourceId: this.id || '',
                didError: false,
              },
            });
          });
        })
        .catch((error) => {
          deferred.reject(error);
          this.dispatchEvent(
            new CartErrorEvent({ error: error && error.message ? error.message : 'Network error', code: 'SERVICE_UNAVAILABLE' })
          );
        })
        .finally(() => {
          if (atcButton) atcButton.disabled = false;
        });
    }

    // The gift is only free if the Shopify discount actually applies to it.
    // If the bands line comes back with a price, take it out again rather
    // than charge for something the page calls FREE.
    async _dropGiftIfNotFree(cart, giftVariantId) {
      const line = cart.items.find(
        (item) => item.variant_id === Number(giftVariantId) && item.properties && item.properties._gift
      );
      if (!line || line.final_line_price === 0) return cart;

      console.warn('[bundle-selector] Gift line was not free; removing it. Check the Free Resistance Bands discount in Shopify Admin.');
      await fetch(Theme.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      });
      return this._refreshCart();
    }

    tierNumbers(tier) {
      const qty = parseInt(tier.dataset.qty, 10) || 1;
      const discount = parseFloat(tier.dataset.discount) || 0;
      const compare = Math.max(this._compareUnitCents || 0, this._unitCents) * qty;
      // Shopify rounds the percentage discount down per unit, then multiplies by quantity.
      const sale = (this._unitCents - Math.floor((this._unitCents * discount) / 100)) * qty;
      return { qty, discount, compare, sale, save: compare - sale };
    }

    renderTier(tier) {
      const n = this.tierNumbers(tier);
      const saleEl = tier.querySelector('[data-sale]');
      const wasEl = tier.querySelector('[data-compare]');
      const saveEl = tier.querySelector('[data-save]');
      if (saleEl) saleEl.textContent = this.formatMoney(n.sale);
      if (wasEl) wasEl.textContent = this.formatMoney(n.compare);
      if (saveEl) saveEl.textContent = 'SAVE ' + this.formatSave(n.save);
    }

    selectTier(tier) {
      const tiers = Array.from(this.querySelectorAll('[data-tier]'));
      tiers.forEach((t) => {
        const on = t === tier;
        t.classList.toggle('is-selected', on);
        const radio = t.querySelector('.bundle-tier__radio');
        if (radio) radio.checked = on;
      });

      const n = this.tierNumbers(tier);
      const qtyInput = this.querySelector('[data-bundle-qty]');
      const propInput = this.querySelector('[data-bundle-prop]');
      const summaryEl = this.querySelector('[data-summary]');

      if (qtyInput) qtyInput.value = n.qty;
      if (propInput) {
        if (n.qty > 1) {
          propInput.disabled = false;
          propInput.value = tier.dataset.label + (n.discount ? ' (save ' + n.discount + '%)' : '');
        } else {
          propInput.disabled = true;
          propInput.value = '';
        }
      }
      if (summaryEl) {
        summaryEl.textContent =
          tier.dataset.label +
          ' · ' +
          this.formatMoney(n.sale) +
          (n.save > 0 ? '  (you save ' + this.formatMoney(n.save) + ')' : '');
      }
      this._updateStickyBar(tier.dataset.label, n.sale, n.compare);
    }

    render() {
      const tiers = Array.from(this.querySelectorAll('[data-tier]'));
      if (!tiers.length) return;
      tiers.forEach((t) => this.renderTier(t));

      let current = tiers.filter((t) => t.classList.contains('is-selected'))[0];
      if (!current) {
        const dt = parseInt(this.dataset.defaultTier, 10) || 1;
        current = tiers[dt - 1] || tiers[0];
      }
      if (current) this.selectTier(current);
    }
  }

  customElements.define('bundle-selector-component', BundleSelectorComponent);
}
